import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { IconComponent } from './icon.component';
import { chipTextColor } from '@sharedlib/sheets-editor/chip-colours';
import {
  type ExistingValidation,
  type ValidationCriteria,
  type ValidationOperator,
  type ValidationOption,
  type ValidationRuleDraft,
} from '@sharedlib/sheets-editor/validation-rules';
import { SHEETS_STRINGS, fill } from '@sharedlib/sheets-editor/sheets.strings';
import { UI_REASONS } from './ui-reasons';
import { asText, setFlag, setOneOf, setText, wrongKind, type UiInspector } from '@sharedlib/sheets-editor/ui-inspector';
import type { SheetsUiValue } from '@sharedlib/common/sheets/ui-commands';

/** Colours offered by the swatch picker — Stitch sheet only (AM-15). */
const SWATCHES: readonly string[] = ['#339995', '#FF9500', '#E40000', '#1B5FFF', '#8C4CD1', '#8FA0B3'];

/**
 * Data-validation panel (AM-29, option B).
 *
 * Stitch-built rather than the engine's own, laid out per mockup frame 04. It only ever
 * emits a rule draft; the host applies it through the engine's public API using the same
 * calls as Stitchy's ops 38-42, so one save is one undoable change.
 */
@Component({
  selector: 'app-validation-panel',
  standalone: true,
  imports: [IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./sheets-panels.css', './sheets-buttons.css'],
  templateUrl: './validation-panel.component.html',
})
export class ValidationPanelComponent implements UiInspector {
  /** NFR-8: the panel's words, from sheets.strings.ts. */
  protected readonly S = SHEETS_STRINGS;
  /** The selection when the panel opened: where a new rule goes. */
  @Input() public set initialRange(value: string) {
    if (!this.replaceId()) this.range.set(value);
  }
  /** The rule already on the selection, loaded to edit; null for a new rule. */
  @Input() public set existing(rule: ExistingValidation | null) {
    this.replaceId.set(rule?.id ?? null);
    if (rule) this.load(rule.form);
  }
  @Output() public readonly done = new EventEmitter<ValidationRuleDraft>();
  @Output() public readonly cancelled = new EventEmitter<void>();
  /** "Remove rule": the id of the rule to delete. */
  @Output() public readonly remove = new EventEmitter<string>();

  protected readonly replaceId = signal<string | null>(null);
  protected readonly range = signal('');
  protected readonly options = signal<ValidationOption[]>([
    { value: '', colour: SWATCHES[0] },
    { value: '', colour: SWATCHES[1] },
  ]);
  protected readonly criteria = signal<ValidationCriteria>('list');
  protected readonly sourceRange = signal('');
  protected readonly operator = signal<ValidationOperator>('between');
  protected readonly value1 = signal('');
  protected readonly value2 = signal('');
  protected readonly formula = signal('');
  protected readonly checkedValue = signal('');
  protected readonly uncheckedValue = signal('');
  protected readonly showAs = signal<'chip' | 'arrow' | 'plain'>('chip');
  protected readonly multiple = signal(false);
  protected readonly allowBlank = signal(true);
  protected readonly onInvalid = signal<'reject' | 'warn'>('reject');
  protected readonly showHelp = signal(false);
  protected readonly helpText = signal('');

  private load(f: Partial<ValidationRuleDraft>): void {
    const set = <T>(sig: { set(v: T): void }, v: T | undefined) => v !== undefined && sig.set(v);
    set(this.range, f.range);
    set(this.criteria, f.criteria);
    set(this.sourceRange, f.sourceRange);
    set(this.operator, f.operator);
    set(this.value1, f.value1);
    set(this.value2, f.value2);
    set(this.formula, f.formula);
    set(this.checkedValue, f.checkedValue);
    set(this.uncheckedValue, f.uncheckedValue);
    // A colour the rule does not have (older rules) takes the first swatch.
    if (f.options) this.options.set(f.options.map((o) => ({ value: o.value, colour: o.colour || SWATCHES[0] })));
    set(this.showAs, f.showAs);
    set(this.multiple, f.multiple);
    set(this.allowBlank, f.allowBlank);
    set(this.onInvalid, f.onInvalid);
    set(this.showHelp, f.showHelp);
    set(this.helpText, f.helpText);
  }

  protected removeRule(): void {
    const id = this.replaceId();
    if (id) this.remove.emit(id);
  }

  /**
   * Parity list (AM-29). Derived from the validators 0.25.2 ships, not from the
   * DataValidationType enum: `time` is in the enum but has no validator, and
   * `listMultiple` is `list` plus the multiple flag rather than its own entry.
   */
  protected readonly criteriaTypes = [
    { key: 'list' as const, label: SHEETS_STRINGS['Dropdown from a list'] },
    { key: 'listRange' as const, label: SHEETS_STRINGS['Dropdown from a range'] },
    { key: 'checkbox' as const, label: SHEETS_STRINGS['Checkbox'] },
    { key: 'whole' as const, label: SHEETS_STRINGS['Whole number'] },
    { key: 'decimal' as const, label: SHEETS_STRINGS['Decimal number'] },
    { key: 'date' as const, label: SHEETS_STRINGS['Date'] },
    { key: 'textLength' as const, label: SHEETS_STRINGS['Text length'] },
    { key: 'custom' as const, label: SHEETS_STRINGS['Custom formula'] },
  ];

  protected readonly operators = [
    { key: 'between' as const, label: SHEETS_STRINGS['is between'] },
    { key: 'notBetween' as const, label: SHEETS_STRINGS['is not between'] },
    { key: 'equal' as const, label: SHEETS_STRINGS['is equal to'] },
    { key: 'notEqual' as const, label: SHEETS_STRINGS['is not equal to'] },
    { key: 'greaterThan' as const, label: SHEETS_STRINGS['is greater than'] },
    { key: 'greaterThanOrEqual' as const, label: SHEETS_STRINGS['is greater than or equal to'] },
    { key: 'lessThan' as const, label: SHEETS_STRINGS['is less than'] },
    { key: 'lessThanOrEqual' as const, label: SHEETS_STRINGS['is less than or equal to'] },
  ];

  protected readonly dateUnsupported: readonly ValidationOperator[] = ['notEqual'];

  protected readonly showAsModes = [
    { key: 'chip' as const, label: SHEETS_STRINGS['Chip'] },
    { key: 'arrow' as const, label: SHEETS_STRINGS['Arrow'] },
    { key: 'plain' as const, label: SHEETS_STRINGS['Plain text'] },
  ];

  /** Each option's name and placeholder: "Option 1", "Option 2"… */
  protected optionLabel(index: number): string {
    return fill('Option {n}', { n: String(index + 1) });
  }

  protected isList(): boolean {
    return this.criteria() === 'list' || this.criteria() === 'listRange';
  }
  protected usesOperator(): boolean {
    const c = this.criteria();
    return c === 'whole' || c === 'decimal' || c === 'date' || c === 'textLength';
  }
  protected needsTwoValues(): boolean {
    return this.operator() === 'between' || this.operator() === 'notBetween';
  }
  protected valueInputType(): string {
    return this.criteria() === 'date' ? 'date' : 'number';
  }
  protected operatorLabel(): string {
    return this.criteria() === 'textLength' ? SHEETS_STRINGS['Text length'] : SHEETS_STRINGS.Condition;
  }

  protected setCriteria(e: Event): void {
    this.pickCriteria((e.target as HTMLSelectElement).value as ValidationCriteria);
  }
  private pickCriteria(next: ValidationCriteria): void {
    this.criteria.set(next);
    // Switching to a date leaves numeric text in the value boxes, which the date
    // input silently blanks; seed something the rule can actually be built from.
    if (next === 'date') {
      const today = new Date().toISOString().slice(0, 10);
      this.value1.set(today);
      this.value2.set(today);
      if (this.dateUnsupported.includes(this.operator())) this.operator.set('between');
    }
  }
  protected setSourceRange(e: Event): void {
    this.sourceRange.set((e.target as HTMLInputElement).value);
  }
  protected setOperator(e: Event): void {
    this.operator.set((e.target as HTMLSelectElement).value as ValidationOperator);
  }
  protected setValue1(e: Event): void {
    this.value1.set((e.target as HTMLInputElement).value);
  }
  protected setValue2(e: Event): void {
    this.value2.set((e.target as HTMLInputElement).value);
  }
  protected setFormula(e: Event): void {
    this.formula.set((e.target as HTMLInputElement).value);
  }
  protected setCheckedValue(e: Event): void {
    this.checkedValue.set((e.target as HTMLInputElement).value);
  }
  protected setUncheckedValue(e: Event): void {
    this.uncheckedValue.set((e.target as HTMLInputElement).value);
  }

  protected setRange(e: Event): void {
    this.range.set((e.target as HTMLInputElement).value);
  }
  protected setHelp(e: Event): void {
    this.helpText.set((e.target as HTMLInputElement).value);
  }
  protected setOnInvalid(e: Event): void {
    this.onInvalid.set((e.target as HTMLSelectElement).value as 'reject' | 'warn');
  }
  protected setOption(index: number, e: Event): void {
    const next = [...this.options()];
    next[index] = { ...next[index], value: (e.target as HTMLInputElement).value };
    this.options.set(next);
  }
  protected cycleColour(index: number): void {
    const next = [...this.options()];
    const at = SWATCHES.indexOf(next[index].colour);
    next[index] = { ...next[index], colour: SWATCHES[(at + 1) % SWATCHES.length] };
    this.options.set(next);
  }
  protected addOption(): void {
    this.options.set([...this.options(), { value: '', colour: SWATCHES[this.options().length % SWATCHES.length] }]);
  }
  protected removeOption(index: number): void {
    this.options.set(this.options().filter((_, i) => i !== index));
  }

  protected emitDone(): void {
    this.done.emit({
      replaceId: this.replaceId(),
      range: this.range(),
      criteria: this.criteria(),
      sourceRange: this.sourceRange(),
      operator: this.operator(),
      value1: this.value1(),
      value2: this.value2(),
      formula: this.formula(),
      checkedValue: this.checkedValue(),
      uncheckedValue: this.uncheckedValue(),
      // An option left blank is not an option.
      options: this.options().filter((o) => o.value.trim() !== ''),
      showAs: this.showAs(),
      multiple: this.multiple(),
      allowBlank: this.allowBlank(),
      onInvalid: this.onInvalid(),
      showHelp: this.showHelp(),
      helpText: this.helpText(),
    });
  }

  // ------------------------------------------- sheets_ui (MCP contract, 6 Oct: "set fields … apply/cancel")

  public uiFields(): Readonly<Record<string, unknown>> {
    return {
      /** Done replaces the selection's own rule, rather than adding one. */
      replacesRule: Boolean(this.replaceId()),
      range: this.range(),
      criteria: this.criteria(),
      sourceRange: this.sourceRange(),
      operator: this.operator(),
      value1: this.value1(),
      value2: this.value2(),
      formula: this.formula(),
      checkedValue: this.checkedValue(),
      uncheckedValue: this.uncheckedValue(),
      options: this.options().map((o) => ({ value: o.value, colour: o.colour })),
      showAs: this.showAs(),
      multiple: this.multiple(),
      allowBlank: this.allowBlank(),
      onInvalid: this.onInvalid(),
      showHelp: this.showHelp(),
      helpText: this.helpText(),
    };
  }

  /** As the panel's own inputs set each field: a date as the date input gives it, a number as the number input. */
  public uiSet(field: string, value: SheetsUiValue): string | null {
    const text = asText(value);
    const needText = (set: (v: string) => void) => setText(field, value, set);
    const needFlag = (set: (v: boolean) => void) => setFlag(field, value, set);
    switch (field) {
      case 'range':
        return needText((v) => this.range.set(v));
      case 'criteria':
        if (!this.criteriaTypes.some((c) => c.key === text)) return wrongKind(field, this.criteriaTypes.map((c) => c.key).join(', '));
        this.pickCriteria(text as ValidationCriteria);
        return null;
      case 'sourceRange':
        return needText((v) => this.sourceRange.set(v));
      case 'operator':
        if (!this.operators.some((o) => o.key === text)) return wrongKind(field, this.operators.map((o) => o.key).join(', '));
        // The date rules have no "is not equal to" (DATE_UNSUPPORTED_OPERATORS): the panel doesn't offer it.
        if (this.criteria() === 'date' && this.dateUnsupported.includes(text as ValidationOperator)) return UI_REASONS.dateHasNo(text);
        this.operator.set(text as ValidationOperator);
        return null;
      case 'value1':
      case 'value2': {
        if (text === null) return wrongKind(field, UI_REASONS.aString);
        const kind = this.valueInputType();
        if (text !== '' && kind === 'date' && !/^\d{4}-\d{2}-\d{2}$/.test(text)) return UI_REASONS.takesDate(field);
        if (text !== '' && kind === 'number' && !Number.isFinite(Number(text))) return UI_REASONS.takesNumber(field);
        (field === 'value1' ? this.value1 : this.value2).set(text);
        return null;
      }
      case 'formula':
        return needText((v) => this.formula.set(v));
      case 'checkedValue':
        return needText((v) => this.checkedValue.set(v));
      case 'uncheckedValue':
        return needText((v) => this.uncheckedValue.set(v));
      case 'options': {
        if (!Array.isArray(value)) return wrongKind(field, UI_REASONS.aList);
        // A colour left out takes the swatch "Add option" would have given that row.
        this.options.set(value.map((o, i) => ({ value: o.value, colour: o.colour ?? SWATCHES[i % SWATCHES.length] })));
        return null;
      }
      case 'showAs':
        return setOneOf(
          field,
          value,
          this.showAsModes.map((m) => m.key),
          (v) => this.showAs.set(v as 'chip' | 'arrow' | 'plain'),
        );
      case 'multiple':
        return needFlag((v) => this.multiple.set(v));
      case 'allowBlank':
        return needFlag((v) => this.allowBlank.set(v));
      case 'onInvalid':
        return setOneOf(field, value, ['reject', 'warn'], (v) => this.onInvalid.set(v as 'reject' | 'warn'));
      case 'showHelp':
        return needFlag((v) => this.showHelp.set(v));
      case 'helpText':
        return needText((v) => this.helpText.set(v));
      default:
        return UI_REASONS.noField('validation', field);
    }
  }

  public uiApply(): string | null {
    this.emitDone();
    return null;
  }

  public uiCancel(): void {
    this.cancelled.emit();
  }

  public uiRemove(): string | null {
    if (!this.replaceId()) return UI_REASONS.newRuleNothingToRemove;
    this.removeRule();
    return null;
  }

  protected chipText(colour: string): string {
    return chipTextColor(colour);
  }
}
